/* import __COLOCATED_TEMPLATE__ from './related-conversations.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';
import type { FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import moment from 'moment-timezone';
import type { Filter, LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { buildFiltersForDataConfig } from 'embercom/lib/reporting/custom/data-config-builder-helpers';
import { restartableTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type AppService from 'embercom/services/app-service';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { tracked } from '@glimmer/tracking';
import { taskFor } from 'ember-concurrency-ts';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { action } from '@ember/object';
import { ProjectedConversationsRanking } from 'embercom/lib/suggestions/constants';
import type IntlService from 'ember-intl/services/intl';

interface Signature {
  Args: {
    suggestedConversationIds: string[];
    impactRanking?: number;
    conversationCount?: number;
    onConversationClicked: (conversation: ConversationExploreConversation) => void;
  };
}

export default class RelatedConversations extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare appService: AppService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingChartService: ReportingChartService;
  @tracked showAll = false;
  @tracked isOpen = true;
  @tracked loadedConversations: ConversationExploreConversation[] = [];

  fetchConversationsTask = taskFor(this.loadKeyConversations);

  conversationData = trackedTask(this, this.fetchConversationsTask, () => [
    this.args.suggestedConversationIds,
  ]);

  get impactBannerText(): string {
    if (this.args.conversationCount === undefined || !this.args.conversationCount) {
      return '';
    }
    let conversationsText = this.intl.t(
      'automation.analyze.task-suggestions.suggestions.impact-banner.conversations-count',
      { count: this.args.conversationCount },
    );
    let weeksText = this.intl.t(
      'automation.analyze.task-suggestions.suggestions.impact-banner.weeks-count',
      { age: this.earliestConversationAgeInWeeks },
    );
    return this.intl.t(
      'automation.analyze.task-suggestions.suggestions.impact-banner.conversations-affected-base',
      { conversationsText, weeksText },
    );
  }

  get impactBannerTitle(): string {
    if (this.args.impactRanking === undefined || this.args.impactRanking === null) {
      return '';
    }
    if (this.args.impactRanking === ProjectedConversationsRanking.High) {
      return this.intl.t(
        'automation.analyze.task-suggestions.suggestions.impact-banner.high-impact',
      );
    } else if (this.args.impactRanking === ProjectedConversationsRanking.Medium) {
      return this.intl.t(
        'automation.analyze.task-suggestions.suggestions.impact-banner.medium-impact',
      );
    }
    return this.intl.t('automation.analyze.task-suggestions.suggestions.impact-banner.low-impact');
  }

  get impactBannerStyling(): string {
    if (this.args.impactRanking === undefined || this.args.impactRanking === null) {
      return '';
    }
    if (this.args.impactRanking === ProjectedConversationsRanking.High) {
      return 'bg-vis-mantis-40 text-on-success';
    } else if (this.args.impactRanking === ProjectedConversationsRanking.Medium) {
      return 'bg-vis-mantis-70 text-success';
    }
    return 'bg-vis-mantis-80 text-success';
  }

  get shouldShowImpactBanner(): boolean {
    return (
      this.args.impactRanking !== undefined &&
      this.args.conversationCount !== undefined &&
      this.args.conversationCount > 0
    );
  }

  get relatedConversations() {
    return this.showAll ? this.loadedConversations : this.loadedConversations.slice(0, 4);
  }

  get shouldSeeShowMoreBtn() {
    return !this.showAll && this.loadedConversations.length > 4;
  }

  get shouldShowConversations() {
    return this.isOpen || this.showAll;
  }

  get conversationsIcon() {
    return this.isOpen ? 'small-thin-down-arrow' : 'small-thin-right-arrow';
  }

  @action
  handleShowMore() {
    this.showAll = true;
  }

  @action
  toggleHeader() {
    if (this.showAll) {
      // If showing all, go back to showing first 4
      this.showAll = false;
      this.isOpen = true;
    } else {
      // Toggle collapse/expand when not showing all
      this.isOpen = !this.isOpen;
    }
  }

  get metric() {
    return this.reportingMetrics.getMetricById('v1.new_conversations');
  }

  getLast12MonthsTimeRange(): FlexibleQueryTimeRange {
    let timezone = this.appService.app.timezone;
    let now = moment.tz(timezone);
    let start = now.clone().subtract(12, 'months').startOf('day').valueOf();
    let end = now.clone().endOf('day').valueOf();

    return {
      start,
      end,
      interval: 'week',
      property: this.metric.timeProperty,
    };
  }

  get attributeIds() {
    return [
      'conversation.id',
      'conversation.first_user.name',
      'conversation.first_user.pseudonym',
      'conversation.first_user_conversation_part_created_at',
      'conversation_custom_fields#ai_insights_conversation_summary',
      'conversation_custom_fields#ai_insights_subtopic',
      'conversation_custom_fields#ai_insights_topic',
      'conversation.ai_generated_metrics.csat_explanation',
      'conversation.ai_generated_metrics.csat',
      'conversation_custom_fields#customer_experience_attributes',
    ];
  }

  get earliestConversationAgeInWeeks(): number {
    if (!this.loadedConversations || this.loadedConversations.length === 0) {
      return 0;
    }

    // Conversations are sorted by time in descending order (most recent first)
    // So the earliest conversation is the last one in the array
    let earliestConversation = this.loadedConversations[this.loadedConversations.length - 1];
    let earliestTime = moment(earliestConversation.time);
    let now = moment.tz(this.appService.app.timezone);
    let weeksDifference = now.diff(earliestTime, 'weeks');

    // Return at least 1 week if there are conversations
    return Math.max(1, Math.ceil(weeksDifference));
  }

  private buildFilterState(suggestedConversationIds: string[]): LogicalFilter {
    let filter: Filter[] = [
      {
        type: 'category',
        data: {
          attribute: 'conversation.id',
          property: 'conversation_id',
          values: suggestedConversationIds,
        },
      },
    ];

    return buildFiltersForDataConfig(
      null,
      this.metric,
      null,
      { type: 'and', filters: filter },
      false,
    ) as LogicalFilter;
  }

  @restartableTask
  *loadKeyConversations(): TaskGenerator<void> {
    if (!this.args.suggestedConversationIds?.length) {
      return;
    }

    let timeRange = this.getLast12MonthsTimeRange();
    let filterState = this.buildFilterState(this.args.suggestedConversationIds);

    let result = yield this.reportingUnderlyingDataService.loadDrillInResult(
      this.appService.app.id,
      this.metric.datasetId,
      filterState,
      0,
      this.args.suggestedConversationIds.length,
      timeRange,
      this.attributeIds,
      'conversation.first_user_conversation_part_created_at', // sortBy
      'desc',
      this.reportingChartService.syntheticDataUseCase,
      this.appService.app.timezone,
      false,
    );

    this.loadedConversations = result.data.map((conversation: any) => ({
      id: Number(conversation['conversation.id']),
      userName: conversation['conversation.first_user.name'],
      userPseudonym: conversation['conversation.first_user.pseudonym'],
      topicSummary: conversation['conversation_custom_fields#ai_insights_conversation_summary'],
      subtopic: conversation['conversation_custom_fields#ai_insights_subtopic'],
      topic: conversation['conversation_custom_fields#ai_insights_topic'],
      cxExplanation: conversation['conversation.ai_generated_metrics.csat_explanation'],
      cxScore: conversation['conversation.ai_generated_metrics.csat'],
      cxAttributes: conversation['conversation_custom_fields#customer_experience_attributes'],
      time: conversation['conversation.first_user_conversation_part_created_at'],
    })) as ConversationExploreConversation[];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::RelatedConversations': typeof RelatedConversations;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/related-conversations': typeof RelatedConversations;
  }
}
